import { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, notification } from 'antd';
import axios from 'axios';

import { IFonteInvestimento, IInvestimentoAtivacao } from '../../../../../../../../InvestimentosACadastrar/interfaces';

import InvestimentosACadastrarService from '../../../../../../../../../../services/http/services/SaldoDeContas/InvestimentosACadastrarService';
import { EmitirErro } from '../../../../../../../../../../services/EmitirErro';

export interface IUseAlterarContaModalProps {
    visible: boolean;
    investimento: IInvestimentoAtivacao | null;
    onClose: () => void;
    onSuccess: (investimentoId: number, novaContaId: number) => void;
}

export const useAlterarContaModal = ({
    visible,
    investimento,
    onClose,
    onSuccess,
}: IUseAlterarContaModalProps) => {
    const [form] = Form.useForm();
    const [loadingFontes, setLoadingFontes] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [fontes, setFontes] = useState<IFonteInvestimento[]>([]);
    const [fonteSelecionadaNumero, setFonteSelecionadaNumero] = useState<string | null>(null);

    const carregarFontes = useCallback(async (investimentoId: number) => {
        const source = axios.CancelToken.source();
        setLoadingFontes(true);
        setFontes([]);
        setFonteSelecionadaNumero(null);

        try {
            const data = await InvestimentosACadastrarService.getFontesParaMoverInvestimento(investimentoId, source.token);
            setFontes(data || []);
        } catch (error) {
            EmitirErro(error);
        } finally {
            setLoadingFontes(false);
        }
    }, []);

    useEffect(() => {
        if (visible && investimento) {
            form.resetFields();
            setFonteSelecionadaNumero(null);
            carregarFontes(investimento.id);
        } else {
            setFontes([]);
            setFonteSelecionadaNumero(null);
            form.resetFields();
        }
    }, [visible, investimento, carregarFontes, form]);

    const handleChangeFonte = useCallback((numero: string) => {
        setFonteSelecionadaNumero(numero || null);
        const fonte = fontes.find((f) => f.numero === numero);
        if (fonte && fonte.contas && fonte.contas.length === 1) {
            form.setFieldsValue({ contaId: fonte.contas[0].id });
        } else {
            form.setFieldsValue({ contaId: undefined });
        }
    }, [fontes, form]);

    const contasDisponiveis = useMemo(() => {
        if (!fonteSelecionadaNumero) return [];
        const fonte = fontes.find((f) => f.numero === fonteSelecionadaNumero);
        return fonte?.contas || [];
    }, [fontes, fonteSelecionadaNumero]);

    const handleFinish = useCallback(async (values: { contaId: number }) => {
        if (!investimento) return;

        setSubmitting(true);
        try {
            await InvestimentosACadastrarService.alterarContaInvestimento({
                investimentoId: investimento.id,
                contaId: values.contaId,
            });

            notification.success({
                message: 'Sucesso',
                description: 'Conta do investimento alterada com sucesso!',
            });

            onSuccess(investimento.id, values.contaId);
            onClose();
        } catch (error) {
            EmitirErro(error);
        } finally {
            setSubmitting(false);
        }
    }, [investimento, onSuccess, onClose]);

    const totalContas = useMemo(
        () => fontes.reduce((acc, fonte) => acc + (fonte.contas?.length || 0), 0),
        [fontes]
    );

    return {
        form,
        loadingFontes,
        submitting,
        fontes,
        fonteSelecionadaNumero,
        contasDisponiveis,
        handleChangeFonte,
        totalContas,
        handleFinish,
    };
};
